import React, { useState, useEffect, useContext } from "react";
import { CustomerCredit, CustomerCreditDetails, postGeneralData, UpdatePaymentComplete, get_order_returnlist } from "../../../Utility/Api";
import { Pagination } from "../../Common/Functions";
import { toast } from "react-toastify";
import { WarehouseContext } from "../../../Utility/UserContext";
import { getFormattedCurrentDate2, NaviGate, onReqCreatePayment, shop_id } from "../../../Utility/function";

/* ─────────────────────────────────────────────
   Unified Customer Transaction Report
   - One screen combining the old two-page flow
   - Summary cards + customer table + pay modal
───────────────────────────────────────────── */
const CustomerRecept = () => {
  const { showLoader, hideLoader, UserItem, shopItem } = useContext(WarehouseContext);

  // ── Customer list ──
  const [customers, setCustomers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const itemsPerPage = 10;

  // ── Selected customer + its sales ──
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [purchasePage, setPurchasePage] = useState(1);
  const [purchaseTotalPages, setPurchaseTotalPages] = useState(1);

  // ── Date filter ──
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // ── Summary computed from sales of selected customer ──
  const [summary, setSummary] = useState({ totalSales: 0, totalPaid: 0, creditBalance: 0, currentBalance: 0 });

  // ── Pay modal ──
  const [payModal, setPayModal] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [cashAmount, setCashAmount] = useState("");
  const [cardAmount, setCardAmount] = useState("");
  const [globalReturns, setGlobalReturns] = useState([]);

  const getOrderReturnedAmount = (orderId, returnsList = globalReturns) => {
    let totalReturned = 0;
    const listToUse = Array.isArray(returnsList) ? returnsList : [];
    listToUse.forEach((ret) => {
      let retData = [];
      try {
        if (ret.return_data) {
          retData = JSON.parse(ret.return_data);
        } else if (ret.invoice_number) {
          retData = JSON.parse(ret.invoice_number);
        }
      } catch (e) { }

      if (Array.isArray(retData)) {
        retData.forEach((retItem) => {
          const isMatch =
            retItem.unique_id === orderId ||
            (ret.unique_id && ret.unique_id.includes(orderId));

          if (isMatch) {
            const qty = parseFloat(retItem.returnqty || retItem.qty || 0);
            const price = parseFloat(retItem.price || 0);
            const amt = parseFloat(retItem.amount || (qty * price) || 0);
            totalReturned += amt;
          }
        });
      }
    });
    return totalReturned;
  };

  const getCustomerReturnedAmount = (customerId, returnsList = globalReturns) => {
    let totalReturned = 0;
    const listToUse = Array.isArray(returnsList) ? returnsList : [];
    listToUse.forEach((ret) => {
      let retData = [];
      try {
        if (ret.return_data) {
          retData = JSON.parse(ret.return_data);
        } else if (ret.invoice_number) {
          retData = JSON.parse(ret.invoice_number);
        }
      } catch (e) { }

      if (Array.isArray(retData)) {
        retData.forEach((retItem) => {
          const itemCustId = retItem.supplier_id || ret.supplier_id;
          if (String(itemCustId) === String(customerId)) {
            const qty = parseFloat(retItem.returnqty || retItem.qty || 0);
            const price = parseFloat(retItem.price || 0);
            const amt = parseFloat(retItem.amount || (qty * price) || 0);
            totalReturned += amt;
          }
        });
      }
    });
    return totalReturned;
  };

  // ── On mount: load customer list ──
  useEffect(() => {
    fetchCustomerList(1, searchQuery);
  }, []);

  useEffect(() => {
    fetchCustomerList(1, searchQuery);
    setCurrentPage(1);
  }, [searchQuery]);

  const fetchCustomerList = async (page, search = searchQuery) => {
    showLoader();
    try {
      const [res, returnsRes] = await Promise.all([
        CustomerCredit(shop_id, page, search),
        get_order_returnlist().catch(e => {
          console.error("Error fetching returns list in fetchCustomerList:", e);
          return { data: { data: [] } };
        })
      ]);

      const returnsList = returnsRes.data?.data || [];
      setGlobalReturns(returnsList);

      if (res.data?.data) {
        setCustomers(res.data.data);
        setTotalPages(res.data.pagination?.total_pages || 1);
      }
    } catch (e) {
      console.error(e);
    } finally {
      hideLoader();
    }
  };

  const handlePageChange = (p) => {
    setCurrentPage(p);
    fetchCustomerList(p, searchQuery);
  };

  // ── When user clicks "View Report" for a customer ──
  const handleViewReport = async (customer) => {
    setSelectedCustomer(customer);
    setPurchasePage(1);
    await fetchSales(customer.customer_id, 1);
  };

  const fetchSales = async (customerId, page) => {
    showLoader();
    try {
      const [res, returnsRes] = await Promise.all([
        CustomerCreditDetails(customerId, shop_id, page),
        get_order_returnlist().catch(e => {
          console.error("Error fetching returns list in fetchSales:", e);
          return { data: { data: [] } };
        })
      ]);

      const returnsList = returnsRes.data?.data || [];
      if (returnsRes.data && returnsRes.data.data) {
        setGlobalReturns(returnsList);
      }

      if (res.data?.data) {
        const data = res.data.data;
        setPurchases(data);
        setPurchaseTotalPages(res.data.pagination?.total_pages || 1);

        // ── Compute summary ──
        const totalSales = data.reduce((s, p) => s + parseFloat(p.overall_price || p.total_amount || 0), 0);
        const totalPaid = data.reduce((s, p) => {
          const returnedAmt = getOrderReturnedAmount(p.order_id, returnsList);
          const remainingBal = Math.max(0, parseFloat(p.remaining || 0) - returnedAmt);
          return s + (parseFloat(p.overall_price || p.total_amount || 0) - remainingBal);
        }, 0);
        const creditBalance = data.reduce((s, p) => {
          const returnedAmt = getOrderReturnedAmount(p.order_id, returnsList);
          const remainingBal = Math.max(0, parseFloat(p.remaining || 0) - returnedAmt);
          return s + remainingBal;
        }, 0);
        setSummary({
          totalSales,
          totalPaid,
          creditBalance,
          currentBalance: creditBalance,
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      hideLoader();
    }
  };

  // ── Pay logic (preserved from Customerdetails.js) ──
  const dueAmount = parseFloat(payTarget?.remaining || 0);
  const totalPayment = parseFloat(cashAmount || 0) + parseFloat(cardAmount || 0);
  const remainingAfterPay = dueAmount - totalPayment;

  const openPayModal = (sale) => {
    setPayTarget(sale);
    setCashAmount("");
    setCardAmount("");
    setPayModal(true);
  };

  const handleCashChange = (e) => {
    const v = e.target.value;
    if (v === "" || (/^\d+$/.test(v) && parseFloat(v) + parseFloat(cardAmount || 0) <= dueAmount)) setCashAmount(v);
  };

  const handleCardChange = (e) => {
    const v = e.target.value;
    if (v === "" || (/^\d+$/.test(v) && parseFloat(v) + parseFloat(cashAmount || 0) <= dueAmount)) setCardAmount(v);
  };

  const onPayCredit = async () => {
    if (!payTarget?.customer_id) { toast.error("Invalid sale details"); return; }
    const cash = parseFloat(cashAmount || 0);
    const card = parseFloat(cardAmount || 0);
    const totalToPay = cash + card;

    if (totalToPay <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    try {
      showLoader();
      let paydata = {
        orderID: payTarget.order_id,
        date: getFormattedCurrentDate2(),
        amount: "0",
        total_amount: payTarget.total_amount,
        cash,
        credit: 0,
        card,
        type: 2,
        mop: cash && card ? "1,4" : cash ? "1" : "4",
        complete: "1",
      };
      if (remainingAfterPay > 0) {
        paydata.credit = remainingAfterPay;
        paydata.mop += ",2";
        paydata.complete = "0";
      }

      await onReqCreatePayment(paydata);

      // Bank debit entry (cash/card received from customer)
      await postGeneralData({
        type: "2",
        ref_id: payTarget.order_id,
        remarks: "Bank - From sales credit",
        date: getFormattedCurrentDate2(),
        created_by: UserItem?.user_id,
        shop_id: shopItem?.id || "0",
        amount: cash + card,
        is_debit: "1",
      });

      if (paydata.complete === "0") {
        await postGeneralData({
          type: "8",
          ref_id: payTarget.order_id,
          remarks: "Sales Credit",
          date: getFormattedCurrentDate2(),
          created_by: UserItem?.user_id,
          shop_id: shopItem?.id || "0",
          amount: remainingAfterPay,
          is_debit: "1",
          profitloss: "",
        });
      } else {
        await UpdatePaymentComplete({ type: "8", shop_id: shopItem?.id || "0", ref_id: payTarget.order_id });
      }

      toast.success("Payment recorded successfully");
      setPayModal(false);
      await fetchSales(selectedCustomer.customer_id, purchasePage);
      fetchCustomerList(currentPage, searchQuery);
    } catch (e) {
      console.error(e);
      toast.error("Error processing payment");
    } finally {
      hideLoader();
    }
  };

  // ── Summary card component ──
  const SummaryCard = ({ label, value, color = "#111827" }) => (
    <div style={{
      background: "#fff",
      border: "1px solid #e5e7eb",
      borderRadius: 12,
      padding: "20px 24px",
      flex: 1,
      minWidth: 160,
    }}>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>{label}</div>
    </div>
  );

  return (
    <div className="page-wrappers">
      <div className="page-content">
        <NaviGate />

        {/* ── Page title ── */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
          <span style={{ fontSize: 20 }}>👤</span>
          <h5 style={{ margin: 0, fontWeight: 700, fontSize: 18 }}>Customer Transaction Report</h5>
        </div>

        {/* ── Filter card ── */}
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "20px 24px", marginBottom: 20 }}>
          <div style={{ fontWeight: 600, marginBottom: 14, fontSize: 14 }}>Select Customer &amp; Date Range</div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
            {/* Customer filter */}
            <div style={{ flex: "1 1 200px" }}>
              <label style={{ fontSize: 12, color: "#374151", marginBottom: 4, display: "block" }}>Customer</label>
              <select
                className="form-select form-select-sm"
                value={selectedCustomer?.customer_id || ""}
                onChange={(e) => {
                  const c = customers.find(x => String(x.customer_id) === e.target.value);
                  if (c) handleViewReport(c);
                  else { setSelectedCustomer(null); setPurchases([]); setSummary({ totalSales: 0, totalPaid: 0, creditBalance: 0, currentBalance: 0 }); }
                }}
              >
                <option value="">All Customers</option>
                {customers.map(c => (
                  <option key={c.customer_id} value={c.customer_id}>{c.customer_name}</option>
                ))}
              </select>
            </div>

            {/* From Date */}
            <div style={{ flex: "1 1 160px" }}>
              <label style={{ fontSize: 12, color: "#374151", marginBottom: 4, display: "block" }}>From Date</label>
              <input type="date" className="form-control form-control-sm" value={fromDate} onChange={e => setFromDate(e.target.value)} />
            </div>

            {/* To Date */}
            <div style={{ flex: "1 1 160px" }}>
              <label style={{ fontSize: 12, color: "#374151", marginBottom: 4, display: "block" }}>To Date</label>
              <input type="date" className="form-control form-control-sm" value={toDate} onChange={e => setToDate(e.target.value)} />
            </div>

            {/* Generate Button */}
            {/* <div style={{ flex: "0 0 auto", display: "none" }}> */}
            <div style={{ flex: "0 0 auto" }}>
              <button
                className="btn btn-sm btn-primary"
                style={{ color: "#fff", borderRadius: 8, padding: "6px 18px", display: "flex", alignItems: "center", gap: 6 }}
                disabled={!selectedCustomer}
                onClick={() => selectedCustomer ? fetchSales(selectedCustomer.customer_id, 1) : fetchCustomerList(1, searchQuery)}
              >
                📄 Generate Report
              </button>
            </div>
          </div>
        </div>

        {/* ── Summary cards (only when a customer is selected) ── */}
        {selectedCustomer && (
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 20 }}>
            <SummaryCard label="Total Sales" value={`${summary.totalSales.toFixed(3)}`} />
            <SummaryCard label="Total Paid" value={`${summary.totalPaid.toFixed(3)}`} color="#16a34a" />
            <SummaryCard label="Credit Balance" value={`${summary.creditBalance.toFixed(3)}`} color="#ea580c" />
            <SummaryCard label="Current Balance" value={`${summary.currentBalance.toFixed(3)}`} color="#dc2626" />
          </div>
        )}

        {/* ── Sales details table (when customer selected) ── */}
        {selectedCustomer ? (
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "20px 24px", marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>
                Sales — <span style={{ color: "#1e3a8a" }}>{selectedCustomer.customer_name}</span>
              </span>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => { setSelectedCustomer(null); setPurchases([]); }}
              >
                ← Back to All Customers
              </button>
            </div>

            <div className="table-responsive">
              <table className="table table-striped table-sm table-bordered align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>S.No</th>
                    <th>Sales No</th>
                    <th>Total Amount (INR)</th>
                    <th>Paid (INR)</th>
                    <th>Due Amount (INR)</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {purchases.length > 0 ? purchases.map((p, i) => {
                    const returnedAmt = getOrderReturnedAmount(p.order_id);
                    const remainingBal = Math.max(0, parseFloat(p.remaining || 0) - returnedAmt);
                    const totalAmt = parseFloat(p.overall_price || p.total_amount || 0);
                    const paid = totalAmt - remainingBal;
                    const isPaid = p.purchase_unit == 1 || remainingBal <= 0;
                    const isFullyReturned = returnedAmt > 0 && remainingBal <= 0;
                    return (
                      <tr key={p.order_id || i}>
                        <td>{(purchasePage - 1) * itemsPerPage + i + 1}</td>
                        <td style={{ fontWeight: 600 }}>{p.order_id}</td>
                        <td>{totalAmt.toFixed(3)}</td>
                        <td style={{ color: "#16a34a", fontWeight: 600 }}>{paid.toFixed(3)}</td>
                        <td style={{ color: remainingBal > 0 ? "#dc2626" : "#16a34a", fontWeight: 600 }}>{remainingBal.toFixed(3)}</td>
                        <td>
                          <span style={{
                            padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                            background: isFullyReturned ? "#fee2e2" : isPaid ? "#dcfce7" : "#fef3c7",
                            color: isFullyReturned ? "#ef4444" : isPaid ? "#16a34a" : "#b45309",
                          }}>
                            {isFullyReturned ? "Returned" : isPaid ? "Paid" : "Pending"}
                          </span>
                        </td>
                        <td>
                          {isFullyReturned ? (
                            <span style={{ color: "#ef4444", fontWeight: 600, fontSize: 13 }}>Returned</span>
                          ) : isPaid ? (
                            <span style={{ color: "#16a34a", fontWeight: 600, fontSize: 13 }}>✓ Cleared</span>
                          ) : (
                            <button className="btn btn-warning btn-sm" onClick={() => openPayModal({ ...p, remaining: remainingBal })}>Pay</button>
                          )}
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr><td colSpan="7" className="text-center text-muted py-3">No sales records found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination currentPage={purchasePage} totalPages={purchaseTotalPages} onPageChange={(p) => { setPurchasePage(p); fetchSales(selectedCustomer.customer_id, p); }} />
          </div>
        ) : (
          /* ── All Customers table ── */
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "20px 24px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <span style={{ fontWeight: 700, fontSize: 15 }}>All Customers</span>
              {/* Search */}
              <div style={{ position: "relative", width: 240 }}>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search customer..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #f3f4f6" }}>
                    <th style={thStyle}>Name</th>
                    <th style={thStyle}>Total Sales</th>
                    <th style={thStyle}>Credit Balance (INR)</th>
                    {/* <th style={thStyle}>Status</th> */}
                    <th style={thStyle}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.length > 0 ? customers.map((c, i) => {
                    const custReturned = getCustomerReturnedAmount(c.customer_id);
                    const adjustedRemaining = Math.max(0, parseFloat(c.total_remaining || 0) - custReturned);
                    return (
                      <tr key={c.customer_id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                        <td style={tdStyle}>{c.customer_name}</td>
                        <td style={tdStyle}>{c.total_sales ?? "—"}</td>
                        <td style={{ ...tdStyle, color: adjustedRemaining > 0 ? "#dc2626" : "#16a34a", fontWeight: 600 }}>
                          {adjustedRemaining.toFixed(3)}
                        </td>
                      {/* <td style={tdStyle}>
                        <span style={{
                          padding: "3px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                          background: "#1e3a8a", color: "#fff",
                        }}>active</span>
                      </td> */}
                      <td style={tdStyle}>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          style={{ fontSize: 12, borderRadius: 6 }}
                          onClick={() => handleViewReport(c)}
                        >
                          View Report
                        </button>
                      </td>
                    </tr>
                  );
                }) : (
                    <tr><td colSpan="5" className="text-center text-muted py-4">No customers found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
          </div>
        )}

        {/* ── Pay modal ── */}
        {payModal && (
          <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.4)" }} tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content p-3" style={{ borderRadius: 12, boxShadow: "0 4px 24px rgba(0,0,0,0.15)" }}>
                <div className="modal-header border-0">
                  <h5 className="modal-title fw-bold">Payment Details</h5>
                  <button type="button" className="btn-close" onClick={() => setPayModal(false)} />
                </div>
                <div className="modal-body">
                  <div className="p-3 bg-light rounded">
                    <p className="mb-2">Sales ID: <strong>{payTarget?.order_id}</strong></p>
                    <p className="mb-2">Customer: <strong>{payTarget?.customer_name || selectedCustomer?.customer_name}</strong></p>
                    <hr />
                    <div className="d-flex justify-content-between mb-2">
                      <span>Total Amount:</span>
                      <strong className="text-primary">{Number(payTarget?.overall_price || payTarget?.total_amount || 0).toFixed(3)}</strong>
                    </div>
                    <div className="d-flex justify-content-between mb-3">
                      <span>Due Amount:</span>
                      <strong className="text-danger">{Number(payTarget?.remaining || 0).toFixed(3)}</strong>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span>Cash:</span>
                      <input type="number" className="form-control text-end" style={{ width: 130 }} value={cashAmount} onChange={handleCashChange} />
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span>Card:</span>
                      <input type="number" className="form-control text-end" style={{ width: 130 }} value={cardAmount} onChange={handleCardChange} />
                    </div>
                    <hr />
                    <div className="d-flex justify-content-between fw-bold fs-6">
                      <span>Remaining After Pay:</span>
                      <span className={remainingAfterPay > 0 ? "text-warning" : "text-success"}>{remainingAfterPay.toFixed(3)}</span>
                    </div>
                  </div>
                </div>
                <div className="modal-footer border-0">
                  <button className="btn btn-secondary" onClick={() => setPayModal(false)}>Cancel</button>
                  <button
                    className="btn btn-success"
                    disabled={totalPayment <= 0 || totalPayment > dueAmount}
                    onClick={onPayCredit}
                  >
                    Pay Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Table style helpers ──
const thStyle = { padding: "10px 14px", fontWeight: 600, fontSize: 13, color: "#374151", background: "#f9fafb" };
const tdStyle = { padding: "10px 14px", fontSize: 13, color: "#374151" };

export default CustomerRecept;
